/**
 * perkie-cart-d21 — the D21 cart's behaviour (port step 7-A).
 * Canon: redesign-brief/canon-export-2026-08-09/Money Flow 6 - The Cart.dc.html.
 * Markup: snippets/perkie-cart.liquid (+ -line, -meter, -roundup).
 *
 * W8  Remove on a piece shows "{title} came out of your cart." with Undo for
 *     8 seconds. Undo re-adds the line with its properties from the row's
 *     own snapshot (data-pc-undo), plus any fee line of that artwork that
 *     assets/perkie-cart-fee.js has since reaped - never a second fee.
 *     Fee and donation rows carry no snapshot: donation removal is silent.
 * W7  Focus in the open amount (memorial quiet variant): the pinned footer
 *     unpins into the end of the scroll region; blur pins it again.
 * W9  The round-up module's show/hide animates across a section re-render.
 * 5B  Once the quiet variant has rendered, the standard card keeps its height
 *     for the session, so nothing jumps.
 * D38 (Port Step 55; ledger PM-343 / PM-344 / PM-345) The note line's Show
 *     all: revealed only on a line whose note is cut (two lines, or the
 *     7-line cap on a one-portrait cart), it opens every note on that line;
 *     the open state is kept by the line's item key and re-applied after
 *     every re-render (afterRender), the precedent being state.open. Only
 *     attributes and classes change here, never child nodes, so the
 *     MutationObserver below never re-enters itself.
 * D38-B (Port Step 55, behind perkie_d38_name_tile) The name on the tile.
 *     Liquid draws it hidden, sized on the card's width model; here each
 *     face a line needs is loaded once (its own Google Fonts family only,
 *     never the list), its state kept on <html> as pf-{face}-ready or
 *     pf-{face}-failed (3 s, then failed for the page view: no late swap),
 *     and once ready every line in it is re-fitted with the card's fitOne /
 *     fitTwo on the MEASURED width (the nameEm gate, PM-343: measured
 *     exceeds the estimate in Trend, Playful and Elegant). A width the
 *     measured size puts under the face's floor gets its hide class (the
 *     hint shows there); no class is ever removed.
 * D38 GA4 (Port Step 62; PM-343 / PM-344 / PM-345, owed by Step 55). Sent the
 *     way the cart bridge sends its own: window.gtag, guarded. Never a
 *     parameter named source, medium or campaign. Reads the DOM and the line's
 *     own Undo snapshot only; writes nothing to the page.
 *     cart_note_expand {surface, pc_pieces, pets, unfolded}: a Show all tap
 *     that opens a line's notes; once per line per open of the cart (the
 *     drawer from opening to closing, the page per view). pets = the notes on
 *     the line; unfolded = the one-portrait-line cap (pc_pieces == 1).
 *     cart_name_state {surface, pc_pieces, state}: once per portrait line
 *     where a name prints (the line carries the hint), per state, per page
 *     view: a re-render of the same cart sends nothing new (keyed on surface,
 *     line key and state). Only from a cart the shopper has open: the page,
 *     or the drawer while it is open. state: none (D38-B off), shown (drawn
 *     on the tile at this width), floor (under the face's floor at this
 *     width, or at every width so Liquid drew the plain hint), ruling (a ruled
 *     no-name line: matched, three pets, two pets apart, over the name cap,
 *     two print areas, half tiles or no mock-up, a gift line), gap (the face
 *     failed or the name could not be measured). A line whose face is still
 *     loading sends when it settles. pc_pieces = the portrait lines on that
 *     surface (piece and gift lines, as perkie-cart.liquid counts them).
 * Chips and the open amount (dark until 7-B): one call per tap, derived from
 *     the cart (the donation line's key and amount ride the module's data
 *     attributes, re-rendered from Liquid on every update). The amount is the
 *     QUANTITY of a $1.00 donation variant, so a chip swap is one atomic
 *     /cart/change.js on the existing line.
 *
 * Event discipline (port brief §2, Gate 1.1): no perkie:* event is listened
 * for or dispatched here. Listeners are delegated on document (click in the
 * capture phase for the remove snapshot, change in the capture phase for the
 * gift note and open amount only, click, mousedown, keydown, input, focusin,
 * focusout) plus a MutationObserver on <cart-drawer> and on the page's
 * <cart-items>, and one on <cart-drawer>'s own class (Dawn's `active`: the
 * drawer opening, for the GA4 events only). After a cart write it publishes Dawn's
 * PUB_SUB_EVENTS.cartUpdate, exactly as perkie-cart-bridge.js does.
 * Double load: the drawer snippet and the cart page both include this file;
 * the in-file flag below is the guard, never a proxy over addEventListener.
 */
(function () {
  'use strict';

  var TOAST_MS = 8000;
  var QUIET_H_KEY = 'perkie_cart_quiet_h';

  /* ---------- Pure helpers (exercised by scripts/s7a-cart-smoke.mjs) ---------- */

  /** The open amount: a plain number, rounded to whole dollars; 0 = invalid. */
  function parseAmount(text) {
    var n = parseFloat(String(text == null ? '' : text).replace(/[^0-9.]/g, ''));
    if (!n || isNaN(n)) return 0;
    var r = Math.round(n);
    return r >= 1 ? r : 0;
  }

  /** A fee line's identity: its artwork (or gift code) and its variant. */
  function feeKey(id, props) {
    props = props || {};
    return [props._perkie_fee_artwork || '', props._gift_code || '', String(id)].join('#');
  }

  /** Items for /cart/add.js on Undo: the piece's fee lines that are no longer
   *  in the cart, then the piece itself (fee first, so the piece lands on top,
   *  the bridge's order). A fee still present is never added twice. */
  function undoItems(snapshot, currentItems) {
    var have = {};
    (currentItems || []).forEach(function (it) {
      var p = it.properties || {};
      if (p._perkie_bundled_with) have[feeKey(it.variant_id || it.id, p)] = true;
    });
    var out = [];
    (snapshot.fees || []).forEach(function (f) {
      var k = feeKey(f.id, f.properties);
      if (have[k]) return;
      have[k] = true;
      out.push({ id: f.id, quantity: f.quantity, properties: f.properties || {} });
    });
    out.push({ id: snapshot.id, quantity: snapshot.quantity, properties: snapshot.properties || {} });
    return out;
  }

  /** The one request a chip tap or an amount makes. d = the module's data. */
  function donationRequest(d, dollars) {
    if (d.key) return { url: '/cart/change.js', body: { id: d.key, quantity: dollars } };
    if (dollars > 0 && d.variant) {
      return {
        url: '/cart/add.js',
        body: {
          items: [{
            id: Number(d.variant),
            quantity: dollars,
            properties: { _perkie_donation: 'true', To: d.to }
          }]
        }
      };
    }
    return null;
  }

  /* D38-B: the card's name rule (frontend/src/components/previewer/d32.jsx NAME_RULE, EM_EST,
   * fitOne, fitTwo), ported line for line with no head box (cx null: the centre; two pets, each
   * half's centre), and the cart's floors (PM-343: Playful 14) and tile widths. */
  var NAME_RULE = { size: 0.24, elegantSize: 0.42, min: 0.12, elegantMin: 0.18, twoMin: 0.08, twoElegantMin: 0.11, twoGap: 0.06 };
  var EM_EST = { elegant: 0.46, other: 0.65 };
  var NM_FLOOR = { classic: 7, preppy: 7, trend: 8, elegant: 11, playful: 14 };
  var NM_WIDTHS = { drawer: [112, 120, 132], page: [112, 160, 176] };
  var NM_FACES = {
    classic: ['Merriweather', 'Merriweather'],
    preppy: ['Graduate', 'Graduate'],
    playful: ['Rampart One', 'Rampart+One'],
    elegant: ['Ms Madi', 'Ms+Madi'],
    trend: ['Fascinate', 'Fascinate']
  };
  var NM_TIMEOUT_MS = 3000;

  function emEstimate(text, face) {
    return String(text || '').length * (face === 'elegant' ? EM_EST.elegant : EM_EST.other);
  }

  function fitOne(em, face) {
    var base = face === 'elegant' ? NAME_RULE.elegantSize : NAME_RULE.size;
    var min = face === 'elegant' ? NAME_RULE.elegantMin : NAME_RULE.min;
    var wfit = em > 0 ? 1 / em : base;
    var size = Math.min(base, wfit);
    if (size < min) size = Math.min(min, wfit);
    var w = size * em;
    return { size: size, x: [Math.min(1 - w / 2, Math.max(w / 2, 0.5))], w: [w] };
  }

  function fitTwo(ems, face) {
    var base = face === 'elegant' ? NAME_RULE.elegantSize : NAME_RULE.size;
    var min = face === 'elegant' ? NAME_RULE.twoElegantMin : NAME_RULE.twoMin;
    var both = ems[0] > 0 && ems[1] > 0;
    var sep = both ? NAME_RULE.twoGap : 0;
    var side = both ? (1 - sep) / 2 : 0.5;
    var fits = ems.map(function (e) { return e > 0 ? Math.min(base, side / e) : base; });
    var sum = Math.max(1e-9, ems[0] + ems[1]);
    var size = Math.min(fits[0], fits[1], (1 - sep) / sum);
    if (size < min) size = Math.min(min, (1 - sep) / sum);
    var w = ems.map(function (e) { return size * e; });
    var c = [0.25, 0.75];
    var x = c.map(function (v, i) { return Math.min(1 - w[i] / 2, Math.max(w[i] / 2, v)); });
    var room = (x[1] - w[1] / 2) - (x[0] + w[0] / 2);
    if (both && room < sep) {
      var d = (sep - room) / 2;
      x[0] -= d;
      x[1] += d;
      if (x[0] < w[0] / 2) { x[0] = w[0] / 2; x[1] = x[0] + (w[0] + w[1]) / 2 + sep; }
      if (x[1] > 1 - w[1] / 2) { x[1] = 1 - w[1] / 2; x[0] = x[1] - (w[0] + w[1]) / 2 - sep; }
    }
    return { size: size, x: x, w: w };
  }

  /** The tile's plan for a line: ems in pet order (0 = no name), its kind, its face, W as a
   *  fraction of the tile (wf) and its surface. k = the name's size as a fraction of the tile's
   *  width; off = the widths (0, 1, 2) where it falls under the face's floor. */
  function tilePlan(ems, kind, face, wf, surface) {
    var g = kind === 'two' ? fitTwo(ems, face) : fitOne(ems[0], face);
    var k = g.size * wf;
    var widths = NM_WIDTHS[surface] || NM_WIDTHS.drawer;
    var off = [];
    for (var i = 0; i < widths.length; i++) {
      if (k * widths[i] < (NM_FLOOR[face] || 7)) off.push(i);
    }
    return { size: g.size, x: g.x, k: k, px: widths.map(function (t) { return k * t; }), off: off };
  }

  var api = { parseAmount: parseAmount, feeKey: feeKey, undoItems: undoItems, donationRequest: donationRequest,
    fitOne: fitOne, fitTwo: fitTwo, tilePlan: tilePlan, emEstimate: emEstimate, NM_FLOOR: NM_FLOOR, NM_WIDTHS: NM_WIDTHS };
  window.__perkieCartD21 = api;

  if (window.__perkieCartD21Loaded) return;
  window.__perkieCartD21Loaded = true;

  var state = { toast: null, timer: null, busy: false, blurT: null, open: {}, notes: {}, opens: 0, expanded: {}, named: {} };
  var roots = [];

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok || (j && j.status && j.status >= 400)) throw j;
        return j;
      });
    });
  }

  /** Header cart count, which Dawn's onCartUpdate does not refresh. */
  function applyBubble(resp) {
    if (!resp || !resp.sections || !resp.sections['cart-icon-bubble']) return;
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;
    try {
      var doc = new DOMParser().parseFromString(resp.sections['cart-icon-bubble'], 'text/html');
      var src = doc.querySelector('.shopify-section');
      if (src) target.innerHTML = src.innerHTML;
    } catch (e) {
      /* the count catches up on the next page view */
    }
  }

  /** Same publish the cart bridge makes after its own add: Dawn's cart-items /
   *  cart-drawer-items re-render themselves, and the fee reaper reconciles. */
  function publishUpdate(resp) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'perkie-cart-d21', cartData: resp });
    }
  }

  /* ---------- W8: the remove toast and Undo ---------- */

  function buildToast(text) {
    var el = document.createElement('div');
    el.className = 'pc-toast';
    el.setAttribute('role', 'status');
    var span = document.createElement('span');
    span.className = 'pc-toast__text';
    span.textContent = text;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pc-toast__undo';
    btn.setAttribute('data-pc-undo-btn', '');
    btn.textContent = 'Undo';
    el.appendChild(span);
    el.appendChild(btn);
    return el;
  }

  /** Put the toast back after any re-render wiped it; clear it elsewhere. */
  function mountToast() {
    var slots = document.querySelectorAll('[data-pc-toast-slot]');
    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];
      var want = state.toast && slot.getAttribute('data-pc-toast-slot') === state.toast.surface;
      var has = slot.querySelector('.pc-toast');
      if (want && !has) slot.appendChild(buildToast(state.toast.text));
      if (!want && has) slot.innerHTML = '';
    }
  }

  function hideToast() {
    clearTimeout(state.timer);
    state.toast = null;
    mountToast();
  }

  function showToast(toast) {
    clearTimeout(state.timer);
    state.toast = toast;
    mountToast();
    state.timer = setTimeout(hideToast, TOAST_MS);
  }

  function doUndo() {
    var t = state.toast;
    if (!t || state.busy) return;
    state.busy = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var drawer = document.querySelector('cart-drawer');
        var useDrawer = !!(drawer && drawer.classList.contains('active') &&
          typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function');
        var body = { items: undoItems(t.snapshot, cart.items), sections_url: window.location.pathname };
        body.sections = useDrawer
          ? drawer.getSectionsToRender().map(function (s) { return s.id; })
          : ['cart-icon-bubble'];
        return post('/cart/add.js', body).then(function (resp) {
          hideToast();
          if (useDrawer && resp.sections) {
            drawer.renderContents(resp);
          } else {
            applyBubble(resp);
          }
          publishUpdate(resp);
        });
      })
      .catch(function (err) {
        /* No failure state is drawn (report gap): the toast stays until it expires. */
        console.error('Perkie cart: undo failed', err);
      })
      .finally(function () {
        state.busy = false;
      });
  }

  /* ---------- W9 + 5B: the module across re-renders ---------- */

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function settleModule(root, surface) {
    var mod = root.querySelector('[data-pc-mod]');
    if (!mod) {
      state.open[surface] = null;
      return;
    }
    var open = mod.getAttribute('data-pc-open') === 'true';
    var prev = state.open[surface];
    state.open[surface] = open;
    if (prev !== true && prev !== false) prev = open;
    if (prev !== open && !reducedMotion()) {
      // The new markup arrived in its final state; paint the old one first,
      // then flip, so the 260ms height transition runs (W9).
      mod.classList.add('pc-mod--still');
      mod.classList.toggle('pc-mod--closed', !prev);
      void mod.offsetHeight;
      mod.classList.remove('pc-mod--still');
      mod.classList.toggle('pc-mod--closed', !open);
    }
    var card = mod.querySelector('[data-pc-mod-card]');
    if (!card) return;
    try {
      if (mod.getAttribute('data-pc-quiet') === 'true') {
        if (card.offsetHeight > 0) window.sessionStorage.setItem(QUIET_H_KEY, String(card.offsetHeight));
      } else {
        var h = parseInt(window.sessionStorage.getItem(QUIET_H_KEY) || '0', 10);
        if (h > 0) card.style.minHeight = h + 'px';
      }
    } catch (e) {
      /* sessionStorage can throw in some privacy modes */
    }
  }

  /* ---------- D38: the note's clamp and Show all ---------- */

  /** True when any note on the line is cut by its clamp or by the line's cap. */
  function notesCut(box) {
    if (box.scrollHeight > box.clientHeight + 1) return true;
    var ps = box.querySelectorAll('.pc-line__note');
    for (var i = 0; i < ps.length; i++) {
      if (ps[i].scrollHeight > ps[i].clientHeight + 1) return true;
    }
    return false;
  }

  function settleNotes(root) {
    var boxes = root.querySelectorAll('[data-pc-notes]');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      var line = box.closest('[data-pc-key]');
      var row = line && line.querySelector('[data-pc-more-row]');
      var btn = row && row.querySelector('[data-pc-more]');
      if (!btn) continue;
      // Not laid out (a closed ancestor): leave it as it is until it is.
      if (!box.getClientRects().length) continue;
      var key = line.getAttribute('data-pc-key') || '';
      // Measured closed, so a note that fits whole never shows Show less.
      box.classList.remove('is-open');
      var cut = notesCut(box);
      var open = cut && state.notes[key] === true;
      if (!cut) delete state.notes[key];
      if (open) box.classList.add('is-open');
      var exp = open ? 'true' : 'false';
      if (btn.getAttribute('aria-expanded') !== exp) btn.setAttribute('aria-expanded', exp);
      if (row.hidden === cut) row.hidden = !cut;
    }
  }

  /* ---------- D38-B: the name on the tile ---------- */

  var faces = {};
  var measure2d = null;

  /** The card's test (d32.jsx faceLoaded): a FontFace of that family has loaded. */
  function faceLoaded(family) {
    try {
      var fs = document.fonts;
      if (!fs || typeof fs.forEach !== 'function') return false;
      var ok = false;
      fs.forEach(function (f) {
        if (!ok && f && f.status === 'loaded' && String(f.family).replace(/["']/g, '').trim() === family) ok = true;
      });
      return ok;
    } catch (e) {
      return false;
    }
  }

  /** The name's width in em of its face, measured (d32.jsx nameEm), or 0 if it cannot be. */
  function measureEm(text, family) {
    try {
      if (!measure2d) {
        var cv = document.createElement('canvas');
        measure2d = cv && cv.getContext ? cv.getContext('2d') : null;
      }
      if (!measure2d) return 0;
      measure2d.font = '100px "' + family + '"';
      var w = measure2d.measureText(text).width / 100;
      return w > 0 && isFinite(w) ? w : 0;
    } catch (e) {
      return 0;
    }
  }

  /** Load one face, once per page view: ready or failed, never both, and never changed after. */
  function loadFace(face) {
    if (faces[face]) return;
    var f = NM_FACES[face];
    var html = document.documentElement;
    var done = false;
    function settle(ok) {
      if (done) return;
      done = true;
      faces[face] = ok ? 'ready' : 'failed';
      html.classList.add('pf-' + face + (ok ? '-ready' : '-failed'));
      settleAllNames();
    }
    faces[face] = 'loading';
    if (!f || !document.fonts || typeof document.fonts.load !== 'function') { settle(false); return; }
    if (faceLoaded(f[0])) { settle(true); return; }
    var timer = setTimeout(function () { settle(false); }, NM_TIMEOUT_MS);
    var fin = function (ok) { clearTimeout(timer); settle(ok); };
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=' + f[1] + '&display=swap';
    link.setAttribute('data-pc-face', face);
    link.onload = function () {
      document.fonts.load('100px "' + f[0] + '"').then(function (list) {
        fin(!!(list && list.length) || faceLoaded(f[0]));
      }, function () { fin(false); });
    };
    link.onerror = function () { fin(false); };
    document.head.appendChild(link);
  }

  /** Re-fit one line on the measured face, then let it show (pc-nm-fit). */
  function fitLine(line, face) {
    var els = line.querySelectorAll('.pc-nm__n');
    var kind = line.getAttribute('data-pc-nm-kind') === 'two' ? 'two' : 'one';
    var names = kind === 'two' ? ['', ''] : [''];
    for (var i = 0; i < els.length; i++) names[parseInt(els[i].getAttribute('data-pc-i'), 10) || 0] = els[i].textContent;
    var family = NM_FACES[face][0];
    var ems = names.map(function (n) { return n ? measureEm(n, family) : 0; });
    for (var j = 0; j < names.length; j++) {
      if (names[j] && !(ems[j] > 0)) { line.classList.add('pc-nm-failed'); return; }
    }
    var plan = tilePlan(ems, kind, face, parseFloat(line.getAttribute('data-pc-nm-wf')) || 0, line.closest('.pc-d') ? 'drawer' : 'page');
    for (var k = 0; k < plan.off.length; k++) line.classList.add('pc-nm-x' + plan.off[k]);
    var row = line.querySelector('.pc-nm');
    if (row) row.style.setProperty('--pc-nm-k', String(Math.round(plan.k * 100000) / 100000));
    for (var m = 0; m < els.length; m++) {
      var idx = parseInt(els[m].getAttribute('data-pc-i'), 10) || 0;
      els[m].style.left = Math.round(plan.x[idx] * 100000) / 1000 + '%';
    }
    line.classList.add('pc-nm-fit');
  }

  function settleNames(root) {
    var lines = root.querySelectorAll('.pc-line--nm');
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (line.classList.contains('pc-nm-fit') || line.classList.contains('pc-nm-failed')) continue;
      var face = line.getAttribute('data-pc-nm') || '';
      if (!NM_FACES[face]) { line.classList.add('pc-nm-failed'); continue; }
      if (!faces[face]) loadFace(face);
      // A face already on the page settles at once and re-runs every cart, this line included.
      if (line.classList.contains('pc-nm-fit') || line.classList.contains('pc-nm-failed')) continue;
      if (faces[face] === 'failed') line.classList.add('pc-nm-failed');
      else if (faces[face] === 'ready') fitLine(line, face);
    }
  }

  function settleAllNames() {
    for (var i = 0; i < roots.length; i++) {
      settleNames(roots[i].el);
      reportNames(roots[i].el, roots[i].surface);
    }
  }

  /* ---------- D38 GA4: cart_note_expand and cart_name_state (Port Step 62) ---------- */

  /** The cart bridge's own call shape (perkie-cart-bridge.js): gtag when it is there, never throwing. */
  function track(name, params) {
    if (typeof window.gtag !== 'function') return;
    try {
      window.gtag('event', name, params);
    } catch (e) {
      /* analytics never reaches the customer */
    }
  }

  /** The portrait lines on a surface: perkie-cart.liquid's pc_pieces (piece and gift lines). */
  function piecesIn(root) {
    return root ? root.querySelectorAll('.pc-line--piece').length : 0;
  }

  function surfaceRoot(el) {
    return el.closest('cart-drawer') || el.closest('cart-items');
  }

  /** The line's item key: data-pc-key on a line with notes, else its stepper's updates[key]. */
  function lineKey(line) {
    var k = line.getAttribute('data-pc-key');
    if (k) return k;
    var q = line.querySelector('input[name^="updates["]');
    if (q) return q.getAttribute('name').slice(8, -1);
    return (line.getAttribute('data-pc-line') || '') + '|' + (line.getAttribute('data-pc-undo') || '');
  }

  function lineProps(line) {
    try {
      return (JSON.parse(line.getAttribute('data-pc-undo') || '{}') || {}).properties || {};
    } catch (e) {
      return {};
    }
  }

  /** D38-B is on: perkie-cart.liquid prints its no-script fallback for the name only then. */
  function nameTileOn(root) {
    if (root.querySelector('.pc-line--nm')) return true;
    var ns = root.querySelectorAll('noscript');
    for (var i = 0; i < ns.length; i++) {
      if ((ns[i].textContent || '').indexOf('pc-line__hint--nm') !== -1) return true;
    }
    return false;
  }

  /** A plain hint with D38-B on: a ruled no-name line (R5 / PM-343 / PM-344, as perkie-cart-line.liquid
   *  decides it), or else Liquid's plan put the name under the face's floor at every width. */
  function ruledNoName(line) {
    if (line.classList.contains('pc-line--gift')) return true;
    if (!line.querySelector('.pc-line__art--mock')) return true;
    if (line.querySelectorAll('.pc-mock__art').length !== 1) return true;
    var p = lineProps(line);
    var str = function (v) { return String(v == null ? '' : v).trim(); };
    var len = function (s) { return Array.from ? Array.from(s).length : s.length; };
    if (p._perkie_composition === 'matched') return true;
    var np = Math.min(3, parseInt(p._perkie_pet_count, 10) || 0);
    for (var i = 2; i <= 3; i++) {
      if ((str(p['Pet ' + i + ' Name']) || str(p['Pet ' + i + ' Notes'])) && np < i) np = i;
    }
    if (np < 1) np = 1;
    if (np === 1) return len(str(p.Name != null && p.Name !== '' ? p.Name : p['Pet 1 Name'])) > 12;
    if (np === 2 && p._perkie_composition === 'combined') {
      return len(str(p['Pet 1 Name'])) > 9 || len(str(p['Pet 2 Name'])) > 9;
    }
    return true;
  }

  /** The line's name state, or '' while its face is still loading. */
  function nameState(line, on) {
    if (!on) return 'none';
    if (line.classList.contains('pc-line--nm')) {
      if (line.classList.contains('pc-nm-failed')) return 'gap';
      if (!line.classList.contains('pc-nm-fit')) return '';
      var nm = line.querySelector('.pc-nm');
      return nm && window.getComputedStyle(nm).display !== 'none' ? 'shown' : 'floor';
    }
    return ruledNoName(line) ? 'ruling' : 'floor';
  }

  function reportNames(root, surface) {
    if (!root || (surface === 'drawer' && !root.classList.contains('active'))) return;
    var lines = root.querySelectorAll('.pc-line--piece');
    if (!lines.length) return;
    var on = nameTileOn(root);
    for (var i = 0; i < lines.length; i++) {
      if (!lines[i].querySelector('.pc-line__hint')) continue;
      var st = nameState(lines[i], on);
      if (!st) continue;
      var k = surface + '|' + lineKey(lines[i]) + '|' + st;
      if (state.named[k]) continue;
      state.named[k] = true;
      track('cart_name_state', { surface: surface, pc_pieces: lines.length, state: st });
    }
  }

  /** After a Show all tap: only when it opened the notes, once per line per open of the cart. */
  function noteExpanded(line) {
    var box = line.querySelector('[data-pc-notes]');
    if (!box || !box.classList.contains('is-open')) return;
    var surface = line.closest('.pc-d') ? 'drawer' : 'page';
    var k = surface + '|' + (line.getAttribute('data-pc-key') || '') + '|' + (surface === 'drawer' ? state.opens : 0);
    if (state.expanded[k]) return;
    state.expanded[k] = true;
    track('cart_note_expand', {
      surface: surface,
      pc_pieces: piecesIn(surfaceRoot(line)),
      pets: box.querySelectorAll('.pc-line__note').length,
      unfolded: box.classList.contains('pc-line__notes--one')
    });
  }

  /** Dawn's drawer opens by adding `active` to <cart-drawer>: a new open for cart_note_expand,
   *  and the drawer's name states report then (never while it is closed). */
  function watchOpen(drawer) {
    if (!drawer || typeof MutationObserver === 'undefined') return;
    var was = drawer.classList.contains('active');
    new MutationObserver(function () {
      var now = drawer.classList.contains('active');
      if (now && !was) {
        state.opens++;
        reportNames(drawer, 'drawer');
      }
      was = now;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function afterRender(root, surface) {
    mountToast();
    settleModule(root, surface);
    settleNotes(root);
    settleNames(root);
    reportNames(root, surface);
  }

  /** Text that moves after a resize or a late web font: re-measure every cart. */
  function settleAll() {
    for (var i = 0; i < roots.length; i++) settleNotes(roots[i].el);
  }

  function observe(root, surface) {
    if (!root || typeof MutationObserver === 'undefined') return;
    roots.push({ el: root, surface: surface });
    new MutationObserver(function () { afterRender(root, surface); })
      .observe(root, { childList: true, subtree: true });
    afterRender(root, surface);
  }

  /* ---------- Chips and the open amount (dark until 7-B) ---------- */

  function readMod(el) {
    var mod = el.closest('[data-pc-mod]');
    if (!mod) return null;
    return {
      el: mod,
      key: mod.getAttribute('data-pc-don-key') || '',
      amount: parseInt(mod.getAttribute('data-pc-don-amount') || '0', 10) || 0,
      variant: mod.getAttribute('data-pc-variant') || '',
      to: mod.getAttribute('data-pc-to') || ''
    };
  }

  function setDonation(d, dollars) {
    if (state.busy) return;
    var req = donationRequest(d, dollars);
    if (!req) return;
    state.busy = true;
    d.el.setAttribute('aria-busy', 'true');
    req.body.sections = ['cart-icon-bubble'];
    req.body.sections_url = window.location.pathname;
    post(req.url, req.body)
      .then(function (resp) {
        applyBubble(resp);
        publishUpdate(resp);
      })
      .catch(function (err) {
        // No network-failure state is drawn (report gap). The drawn amount
        // error says "Try a plain number", which would be false here, so a
        // failed tap simply leaves the cart as it was.
        console.error('Perkie cart: donation update failed', err);
      })
      .finally(function () {
        state.busy = false;
        d.el.removeAttribute('aria-busy');
      });
  }

  function commitAmount(el) {
    var d = readMod(el);
    if (!d) return;
    var input = d.el.querySelector('[data-pc-amount]');
    var n = parseAmount(input ? input.value : '');
    if (!n) {
      d.el.classList.add('pc-mod--err');
      return;
    }
    d.el.classList.remove('pc-mod--err');
    setDonation(d, n);
  }

  /* ---------- Delegated listeners ---------- */

  // Dawn's <cart-items> / <cart-drawer-items> treat EVERY change event inside
  // them as a quantity edit (cart.js validateQuantity), and in the D21 layout
  // the gift note and the open amount sit inside them. Without this, blurring
  // either fires a quantity "step" validation bubble on a text field. Scoped to
  // those two fields; quantity inputs are untouched.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('[data-pc-amount], #CartDrawer-Note, #Cart-note')) e.stopPropagation();
  }, true);

  // Capture phase: runs before Dawn's CartRemoveButton handler on the element
  // itself, so the row (and its snapshot) still exists. Never stops the event.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('cart-remove-button') : null;
    if (!btn) return;
    var row = btn.closest('[data-pc-undo]');
    if (!row) return;
    var snapshot;
    try {
      snapshot = JSON.parse(row.getAttribute('data-pc-undo'));
    } catch (err) {
      return;
    }
    showToast({
      text: (row.getAttribute('data-pc-title') || '') + ' came out of your cart.',
      snapshot: snapshot,
      surface: row.closest('.pc-d') ? 'drawer' : 'page'
    });
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    if (t.closest('[data-pc-undo-btn]')) {
      doUndo();
      return;
    }
    var more = t.closest('[data-pc-more]');
    if (more) {
      var mline = more.closest('[data-pc-key]');
      if (!mline) return;
      var opening = more.getAttribute('aria-expanded') !== 'true';
      state.notes[mline.getAttribute('data-pc-key') || ''] = opening;
      settleNotes(mline);
      if (opening) noteExpanded(mline);
      return;
    }
    var chip = t.closest('[data-pc-chip]');
    if (chip) {
      var d = readMod(chip);
      if (!d) return;
      var a = parseInt(chip.getAttribute('data-pc-chip'), 10) || 0;
      // Tapping the chosen chip again removes the donation, silently.
      setDonation(d, d.amount === a ? 0 : a);
      return;
    }
    if (t.closest('[data-pc-amount-add]')) commitAmount(t);
  });

  // The Add button must not blur the field first (W7's footer would re-pin).
  document.addEventListener('mousedown', function (e) {
    if (e.target && e.target.closest && e.target.closest('[data-pc-amount-add]')) e.preventDefault();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || !e.target || !e.target.closest) return;
    var input = e.target.closest('[data-pc-amount]');
    if (!input) return;
    e.preventDefault(); // never submit the cart form from the amount field
    commitAmount(input);
  });

  document.addEventListener('input', function (e) {
    var input = e.target && e.target.closest ? e.target.closest('[data-pc-amount]') : null;
    if (!input) return;
    var mod = input.closest('[data-pc-mod]');
    if (mod) mod.classList.remove('pc-mod--err');
  });

  document.addEventListener('focusin', function (e) {
    var input = e.target && e.target.closest ? e.target.closest('[data-pc-amount]') : null;
    if (!input) return;
    var drawer = input.closest('.pc-d');
    if (!drawer) return;
    clearTimeout(state.blurT);
    drawer.classList.add('pc-d--typing');
    var scroller = input.closest('[data-pc-scroll]');
    if (!scroller) return;
    setTimeout(function () {
      var r = input.getBoundingClientRect();
      var s = scroller.getBoundingClientRect();
      scroller.scrollTop += r.top - s.top - 120;
    }, 30);
  });

  document.addEventListener('focusout', function (e) {
    var input = e.target && e.target.closest ? e.target.closest('[data-pc-amount]') : null;
    if (!input) return;
    var drawer = input.closest('.pc-d');
    if (!drawer) return;
    state.blurT = setTimeout(function () {
      drawer.classList.remove('pc-d--typing');
    }, 120);
  });

  function init() {
    watchOpen(document.querySelector('cart-drawer'));
    observe(document.querySelector('cart-drawer'), 'drawer');
    observe(document.querySelector('cart-items'), 'page');
    var rt = null;
    if (window.addEventListener) {
      window.addEventListener('resize', function () {
        clearTimeout(rt);
        rt = setTimeout(settleAll, 120);
      });
    }
    try {
      if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', settleAll);
    } catch (e) {
      /* no font events: the clamp is measured at render */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
